import { Component, HostListener, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { AlertService } from '../../core/services/alert.service';
import { AuthService } from '../../core/services/auth.service';
import { ConfigService } from '../../core/services/config.service';
import { NotificationCenterService } from '../../core/services/notification-center.service';
import { NotificationService } from '../../core/services/notification.service';
import { NotificationItem } from '../../core/models/notification-center.model';
import { SocketConnectionService } from '../../core/sockets/socket-connection.service';
import { ThemeService } from '../../core/services/theme.service';
import { SidebarStateService } from './sidebar-state.service';
import { AboutDialogComponent } from '../../features/about/about-dialog.component';
import { InternalSearchComponent } from '../../shared/internal-search/internal-search.component';
import { NotificationPanelComponent } from '../../shared/components/notifications/notification-panel.component';
import { VerificarApiComponent } from '../../shared/verificar-api/verificar-api.component';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    RouterLink,
    AboutDialogComponent,
    InternalSearchComponent,
    NotificationPanelComponent,
    VerificarApiComponent,
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent implements OnInit {
  private readonly alertService = inject(AlertService);
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly socketConnectionService = inject(SocketConnectionService);
  private readonly notificationCenter = inject(NotificationCenterService);
  private readonly notificationService = inject(NotificationService);
  private readonly themeService = inject(ThemeService);
  private readonly sidebarState = inject(SidebarStateService);
  private readonly router = inject(Router);
  private readonly isMenuOpen = signal(false);
  private readonly isNotificationPanelOpen = signal(false);

  protected readonly themeMode = this.themeService.currentMode;
  protected readonly menuOpen = this.isMenuOpen.asReadonly();
  protected readonly notificationPanelOpen = this.isNotificationPanelOpen.asReadonly();
  protected readonly currentUser = this.authService.user;
  protected readonly notifications = this.notificationCenter.items;
  protected readonly notificationsState = this.notificationCenter.visualState;
  protected readonly notificationsError = this.notificationCenter.error;
  protected readonly unreadNotifications = this.notificationCenter.unreadCount;
  protected readonly notificationsLoadingMore = this.notificationCenter.loadingMore;
  protected readonly notificationsHasMore = this.notificationCenter.hasMore;
  protected aboutVisible = false;
  protected verificarApiVisible = false;

  ngOnInit(): void {
    if (!this.socketConnectionService.isConnected()) {
      this.socketConnectionService.InicializarConnection();
    }
    this.notificationCenter.initialize();
  }

  protected toggleSidebar(): void {
    this.sidebarState.toggle();
  }

  protected toggleTheme(): void {
    this.themeService.toggle();
  }

  protected toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.isMenuOpen.update((value) => !value);
  }

  protected openAbout(): void {
    this.isMenuOpen.set(false);
    this.aboutVisible = true;
  }

  protected openVerificarApi(): void {
    this.isMenuOpen.set(false);
    this.verificarApiVisible = true;
  }

  protected actualizarPermisos(): void {
    this.isMenuOpen.set(false);
    const user = this.currentUser();
    if (!user) {
      return;
    }

    void this.alertService
      .confirm({
        title: 'Actualizar permisos',
        text: '¿Confirma la actualización de los permisos almacenados?',
        confirmText: 'Actualizar',
      })
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }

        const idUsuario = Number(user.id);
        const idSucursal = user.sucursalDeCreacion?.Id;

        this.authService.getPermisos(idUsuario, idSucursal).subscribe({
          next: (response) => {
            if (response.Succeeded) {
              this.authService.setPermisos(response.Data);
              this.notificationService.success('Permisos actualizados');
              window.location.reload();
            } else {
              this.notificationService.error(
                'Ha sucedido un error en la actualización de permisos.',
              );
            }
          },
          error: () => {
            this.notificationService.error('Ha sucedido un error en la actualización de permisos.');
          },
        });
      });
  }

  protected openProfile(): void {
    this.isMenuOpen.set(false);
    void this.router.navigate(['perfil']);
  }

  protected openConfiguracion(): void {
    this.isMenuOpen.set(false);
    void this.router.navigate(['configuracion']);
  }

  protected openNovedades(): void {
    this.isMenuOpen.set(false);
    void this.router.navigate(['info', 'novedades']);
  }

  protected openCentroSoporte(): void {
    this.isMenuOpen.set(false);
    const url = this.configService.getAppConfig().url_centro_soporte;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  protected logout(): void {
    this.isMenuOpen.set(false);
    this.isNotificationPanelOpen.set(false);
    this.notificationCenter.clear();
    this.socketConnectionService.desconectarConnection();
    this.authService.logout();
  }

  @HostListener('document:click')
  protected closeUserMenu(): void {
    this.isMenuOpen.set(false);
  }

  protected toggleNotificationPanel(): void {
    this.isNotificationPanelOpen.update((open) => !open);
  }

  protected closeNotificationPanel(): void {
    this.isNotificationPanelOpen.set(false);
  }

  protected retryNotificationsLoad(): void {
    void this.notificationCenter.reload();
  }

  protected loadMoreNotifications(): void {
    void this.notificationCenter.loadMore();
  }

  protected openNotification(item: NotificationItem): void {
    this.notificationCenter.markAsRead(item.id);
    this.closeNotificationPanel();

    if (item.target?.externalUrl) {
      window.open(item.target.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (item.target?.route) {
      void this.router.navigateByUrl(item.target.route);
    }
  }
}
